import {
  AHEmptyScreen,
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  ListItem,
} from "arkh";
import { useLazyLoadQuery, graphql } from "react-relay";
import { useSetCurrentStoreId } from "../../../OS/Login/State";
import { FlatList, Platform, StyleSheet, View } from "react-native";
import { OrgHomeScreenProps } from "../OrgHomeNavigation";
import { useCallback, useMemo, useState } from "react";
import { useOrgHomeDetailLayout } from "../useOrgHomeDetailContainerStyle";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { Features, hasFeatureInTier } from "../../../useHasFeatureInTier";
import { OrgStoresQuery } from "./__generated__/OrgStoresQuery.graphql";
import { OrgSearchInput } from "../OrgSearchInput";
import { listGutterStyle } from "../guterStyle";

import { WorkspaceEmptyListItem } from "../WorkspaceEmptyListItem";
import { useWorkspaceId } from "../workspaceId";
import { OrgStoreWorkspaceBreadcrumbs } from "./OrgStoreWorkspaceBreadcrumbs";
import { Image } from "expo-image";
import { FormattedMessage } from "react-intl";
import { OrgTableTitle } from "../OrgTable/OrgTable";
import { WorkspaceDetailNavigationHeader } from "../WorkspaceDetailNavigationHeader";
import { useFilterSearch } from "@/src/components/mobile/useSearch";
import { OrgStoresMap } from "./OrgStoresMap";
import { OrgStoresMapActiveUsers } from "./OrgStoresMapActiveUsers";

export function OrgStoresScreen(props: OrgHomeScreenProps<"OrgStores">) {
  return (
    <Box style={{ flex: 1 }} color="surface-background-light">
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey: string) => (
          <InnerOrgStoresScreen {...props} fetchKey={fetchKey} />
        )}
      </LazyQueryNetworkBoundrary>
    </Box>
  );
}

function InnerOrgStoresScreen(
  props: OrgHomeScreenProps<"OrgStores"> & { fetchKey: string }
) {
  const intl_tokens = useIntlTokens();
  const [search, setSearch] = useState("");
  const workspaceId = useWorkspaceId();
  const data = useLazyLoadQuery<OrgStoresQuery>(
    graphql`
      query OrgStoresQuery($workspaceId: ID!, $includeMapData: Boolean!) {
        organisation {
          name
          features
          ...OrgStoresMapActiveUsers_organisation @include(if: $includeMapData)
        }
        workspace(id: $workspaceId) {
          stores {
            id
            name
            tier
            timezone
            externalId

            workspaceBreadcrumbs(
              belowWorkspaceId: $workspaceId
              includeExternalId: true
            )
            ...OrgStoreWorkspaceBreadcrumbs_store
              @arguments(currentWorkspaceId: $workspaceId)
            ...OrgStoresMap_stores
          }
        }
      }
    `,
    {
      workspaceId,
      includeMapData: Platform.OS === "web",
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const sortedStores = useMemo(() => {
    return (
      data.workspace.stores?.slice().sort((a, b) => {
        const aBc = a.workspaceBreadcrumbs.join(" ").toLowerCase();
        const bBc = b.workspaceBreadcrumbs.join(" ").toLowerCase();
        if (aBc === bBc) {
          return a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()
            ? 1
            : -1;
        }

        return aBc > bBc ? 1 : -1;
      }) ?? []
    );
  }, [data.workspace.stores]);
  const mapper = useCallback((store: (typeof sortedStores)[number]) => {
    return `${store.externalId} ${store.name}`;
  }, []);
  const [, listData] = useFilterSearch(search, sortedStores, mapper);
  const setCurrentStoreId = useSetCurrentStoreId();
  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <ListItem
          displayMode="TABLE"
          border="border-primary"
          isLast={index === listData.length - 1}
          left={<AHIcon type="store" color="text-washed" />}
          right={
            <>
              <ListItem.NavArrow />
            </>
          }
          onPress={() => {
            setCurrentStoreId(item.id, item.timezone);

            if (hasFeatureInTier(Features.OPERATIONS_PLATFORM, item.tier)) {
              props.navigation.navigateDeprecated("Team", {});
            } else {
              props.navigation.navigateDeprecated("Dashboard", {});
            }
          }}
        >
          <OrgStoreWorkspaceBreadcrumbs store={item} />
          <Box
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <OrgTableTitle>{item.name}</OrgTableTitle>
          </Box>
        </ListItem>
      );
    },
    [listData.length, props.navigation, setCurrentStoreId]
  );

  const detailLayout = useOrgHomeDetailLayout();

  const orgStoresDescription = (
    <FormattedMessage
      defaultMessage="Here are all active units within the current workspace listed. By clicking on units you can teleport yourself to their view and check operational status in realtime. You need administrator capabilities in order to create new units."
      description="OrgStores Description"
    />
  );

  const showMap =
    Platform.OS === "web" &&
    (data.organisation?.features.includes("MAP") ?? false);

  if (data.workspace.stores.length === 0) {
    return (
      <AHEmptyScreen
        title={intl_tokens.Units}
        subtitle={orgStoresDescription}
        illustration={
          <Box style={{ flex: 1 }}>
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./no_units.png")}
            />
          </Box>
        }
      />
    );
  }

  const storeList = (
    <FlatList
      style={[
        { flex: 1, marginBottom: detailLayout.keyboardBottomInset },
        listGutterStyle,
      ]}
      contentContainerStyle={
        showMap ? styles.listContent : detailLayout.contentContainerStyle
      }
      data={listData}
      keyboardShouldPersistTaps="handled"
      renderItem={renderItem}
      ItemSeparatorComponent={AHSeparator}
      stickyHeaderIndices={[0]}
      ListEmptyComponent={WorkspaceEmptyListItem}
      ListHeaderComponent={
        <Box color="surface-background-light" spaceTop="medium">
          <OrgSearchInput value={search} onChangeText={setSearch} />
          <Box color="surface-background-light">
            <ListItem disabled border="border-primary">
              <OrgTableTitle>{intl_tokens.Name}</OrgTableTitle>
            </ListItem>
            <AHSeparator />
          </Box>
        </Box>
      }
    />
  );

  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={<AHText type="subtitle">{intl_tokens.Units}</AHText>}
        right={null}
      />
      {showMap ? (
        <View style={styles.splitContainer}>
          <View style={styles.listPane}>{storeList}</View>
          <View style={styles.mapPane}>
            {data.organisation && (
              <OrgStoresMapActiveUsers organisation={data.organisation} />
            )}
            <OrgStoresMap stores={data.workspace.stores} />
          </View>
        </View>
      ) : (
        storeList
      )}
    </>
  );
}

const styles = StyleSheet.create({
  splitContainer: {
    flex: 1,
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 16,
  },
  listPane: {
    flex: 1,
    maxWidth: 900,
  },
  mapPane: {
    flex: 1,
    paddingTop: 16,
    position: "relative",
  },
  listContent: {
    paddingBottom: 48,
  },
});
